import 'react-autocomplete-input/dist/bundle.css';

import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  isContentId,
  useContent,
  useContentContext,
  useContents,
} from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { typography } from 'src/core/themes/shared.css';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { getColumnPersonalization } from 'src/utils/columns/columnPersonalizationUtils';
import {
  editableFormulaToTokens,
  fromExecutableFormula,
  tagKeyToFieldName,
  toExecutableFormula,
} from 'src/utils/columns/customColumnUtils';
import {
  CustomEventsColumn,
  CustomOnSaleEventsColumn,
} from 'src/utils/columns/events/eventsCustomColumnUtils.types';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { CustomMarketplacePaymentsColumn } from 'src/utils/columns/payments/paymentsCustomColumnUtils.types';
import { CustomPurchasesColumn } from 'src/utils/columns/purchases/purchasesCustomColumnUtils.types';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { ContentIds } from 'src/utils/constants/contentIdDataMap';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { UserSetting } from 'src/WebApiController';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../../Modal';
import * as styles from '../TableColumns.css';
import { CustomColumnInstructionPopover } from './Components/CustomColumnInstructionPopover';
import { FormulaBuilderInput } from './Components/FormulaBuilderInput';
import { CustomColumnType } from './TableCustomColumnModal.types';
import {
  getColumnIdFromFormulaFieldId,
  getSectionTypeColumnFns,
  SECTION_TYPE_TO_PRESET_COLUMNS,
  sectionTypeToActionOutboxEntityType,
  validateNewCustomColumn,
} from './TableCustomColumnModal.utils';

export function TableCustomColumnModal<
  T extends
    | CustomListingColumn
    | CustomSalesColumn
    | CustomEventsColumn
    | CustomPurchasesColumn
    | CustomMarketplacePaymentsColumn
    | CustomOnSaleEventsColumn,
>({
  onClose,
  onSave,
  columnToEditId,
  sectionType,
}: {
  onClose: () => void;
  onSave: (columns: T[]) => void;
  columnToEditId?: string;
  sectionType: SectionType;
}) {
  const [isOpen, toggleIsOpen] = useToggle(true);
  const [showMetricSelector, setShowMetricSelector] = useState(false);
  const { appContext, loginContext } = useAppContext();
  const { contentResolver } = useContentContext();

  const nameColumnPlaceholder = useContent(ContentId.NameColumnPlaceholder);
  const requiredMsg = useContent(ContentId.Required);
  const invalidFormulaMsg = useContent(ContentId.InvalidFormula);
  const columnWithSameNameExistsMsg = useContent(
    ContentId.ColumnWithSameNameExists
  );

  const allPresetColumnsMap = SECTION_TYPE_TO_PRESET_COLUMNS[
    sectionType
  ] as Record<string, ContentId>;

  const allPresetColumnCid = new Set([...Object.values(allPresetColumnsMap)]);
  const allPresetColumnNames = useContents(
    Array.from(allPresetColumnCid.values())
  );

  const actionOutboxEntityType =
    sectionTypeToActionOutboxEntityType(sectionType);

  const { tagsMetadataNumeric } = useTagsForEntityType(
    actionOutboxEntityType,
    true
  );

  const {
    findDependentListingTableColumnIds,
    validateFormula,
    fieldOptionsPreset,
  } = getSectionTypeColumnFns(sectionType);

  const filteredFormulaFields = useMemo(
    () =>
      Object.entries(fieldOptionsPreset)
        .filter(([field]) => {
          const columnId = getColumnIdFromFormulaFieldId(sectionType, field);

          const columnDef = getColumnPersonalization({
            id: columnId ?? '',
            sectionType,
          });
          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures
            );
          return hasFeatureForColumn;
        })
        .reduce<Record<string, ContentId>>(
          (acc, [field, contentId]) => ({
            ...acc,
            [field]: contentId,
          }),
          {}
        ),
    [appContext?.features, fieldOptionsPreset, loginContext?.user, sectionType]
  );

  const fieldOptions = useMemo(
    () => ({
      ...filteredFormulaFields,
      ...tagsMetadataNumeric?.reduce<Record<string, string>>(
        (acc, tag) => ({
          ...acc,
          [tagKeyToFieldName(tag.key)]: tag.key,
        }),
        {}
      ),
    }),
    [filteredFormulaFields, tagsMetadataNumeric]
  );

  const fieldOptionsReverse = useMemo(() => {
    return Object.entries(fieldOptions).reduce<Record<string, string>>(
      (acc, entry) => {
        const [key, value] = entry;
        if (!isContentId(value)) {
          acc[value] = key;
          return acc;
        }

        const content = contentResolver(
          ContentIds[value as ContentId]
        ) as string;
        acc[content] = key;
        return acc;
      },
      {}
    );
  }, [contentResolver, fieldOptions]);

  const autoCompleteOptions = useMemo(
    () => Object.keys(fieldOptionsReverse),
    [fieldOptionsReverse]
  );

  const { value: customEventsColumns = [] } = useServerUserSetting<
    CustomEventsColumn[]
  >({
    id: UserSetting.EventsCustomColumns,
  });
  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: UserSetting.InventoryCustomColumns,
  });
  const { value: customSaleColumns = [] } = useServerUserSetting<
    CustomSalesColumn[]
  >({
    id: UserSetting.SaleCustomColumns,
  });
  const { value: customPurchaseColumns = [] } = useServerUserSetting<
    CustomSalesColumn[]
  >({
    id: UserSetting.PurchaseCustomColumns,
  });
  const { value: customPaymentsColumns = [] } = useServerUserSetting<
    CustomMarketplacePaymentsColumn[]
  >({
    id: UserSetting.MarketplacePaymentsCustomColumns,
  });
  const { value: customOnSaleEventColumns = [] } = useServerUserSetting<
    CustomOnSaleEventsColumn[]
  >({
    id: UserSetting.OnSaleEventCustomColumns,
  });

  const customColumns = {
    [SectionType.Events]: customEventsColumns,
    [SectionType.Listings]: customListingColumns,
    [SectionType.ListingsFlattened]: customListingColumns,
    [SectionType.ListingsReport]: customListingColumns,
    [SectionType.InventorySideTable]: customListingColumns,
    [SectionType.Sales]: customSaleColumns,
    [SectionType.SalesFlattened]: customSaleColumns,
    [SectionType.SalesReport]: customSaleColumns,
    [SectionType.SalesSideTable]: customSaleColumns,
    [SectionType.Purchases]: customPurchaseColumns,
    [SectionType.PurchaseEvent]: customPurchaseColumns,
    [SectionType.PurchaseSideTable]: customPurchaseColumns,
    [SectionType.MarketplacePaymentsTable]: customPaymentsColumns,
    [SectionType.OnSaleEvents]: customOnSaleEventColumns,
    [SectionType.RecentSalesTable]: [],
  }[sectionType] as T[];

  const methods = useForm<CustomColumnType>({
    defaultValues: columnToEditId
      ? customColumns.find((c) => c.id === columnToEditId)
      : { id: nanoid() },
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = methods;

  const name = watch('name');
  const [formulaTokens, setFormulaTokens] = useState<string[]>([]);
  const [formulaCaretPosition, setFormulaCaretPosition] = useState<number>(-1);

  const onSaveHandler = (newCustomColumn: CustomColumnType) => {
    newCustomColumn.name = newCustomColumn.name.trim();
    newCustomColumn.formula = toExecutableFormula(formulaTokens ?? []).trim();

    if (
      !validateNewCustomColumn(
        newCustomColumn.name,
        newCustomColumn.formula,
        allPresetColumnNames,
        customColumns,
        columnToEditId,
        tagsMetadataNumeric,
        setError,
        columnWithSameNameExistsMsg,
        invalidFormulaMsg,
        requiredMsg,
        validateFormula
      )
    )
      return;

    const editableFormula = fromExecutableFormula(newCustomColumn.formula);
    newCustomColumn.dependency = findDependentListingTableColumnIds(
      editableFormula ?? ''
    );

    onSave([
      ...customColumns.filter((c) =>
        columnToEditId ? c.id !== columnToEditId : true
      ),
      newCustomColumn,
    ] as T[]);

    toggleIsOpen();
  };

  useEffect(() => {
    // Set the text value on load
    if (columnToEditId) {
      const formula = customColumns.find(
        (c) => c.id === columnToEditId
      )?.formula;
      if (formula && autoCompleteOptions.length) {
        setFormulaTokens(
          editableFormulaToTokens(
            fromExecutableFormula(formula),
            fieldOptionsReverse
          )
        );
      }
    }
  }, [
    columnToEditId,
    customColumns,
    autoCompleteOptions.length,
    fieldOptionsReverse,
  ]);

  useEffect(() => {
    if (!isOpen) setTimeout(onClose, 300);
  }, [isOpen, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={toggleIsOpen}
      unmountOnClose
      centered
      size="sm"
      onClick={() => {
        // hide caret
        setFormulaCaretPosition(-1);
        setShowMetricSelector(false);
      }}
    >
      <ModalHeader onClose={toggleIsOpen}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            {columnToEditId ? (
              <Content id={ContentId.EditACustomColumn} />
            ) : (
              <Content id={ContentId.CreateACustomColumn} />
            )}
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.bodyContainer}>
          <div className={styles.title}>
            <Content id={ContentId.Name} />
          </div>
          <PosFormField errors={errors.name?.message}>
            <PosTextField
              value={name}
              placeholder={nameColumnPlaceholder}
              {...register('name')}
              onChange={(e) => {
                clearErrors('name');
                setValue('name', e.target.value);
              }}
            />
          </PosFormField>
          <div className={styles.title}>
            <Content id={ContentId.FormulaBuilder} />

            <CustomColumnInstructionPopover />
          </div>
          <FormProvider {...methods}>
            <FormulaBuilderInput
              fieldOptionsReverse={fieldOptionsReverse}
              formulaTokens={formulaTokens}
              setFormulaTokens={setFormulaTokens}
              formulaCaretPosition={formulaCaretPosition}
              setFormulaCaretPosition={setFormulaCaretPosition}
              showMetricSelector={showMetricSelector}
              setShowMetricSelector={setShowMetricSelector}
            />
          </FormProvider>
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton textContentId={ContentId.Cancel} onClick={toggleIsOpen} />
        <OkButton
          onClick={handleSubmit(onSaveHandler)}
          textContentId={ContentId.Save}
        />
      </ModalFooter>
    </Modal>
  );
}
